import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useFetchArtistCountries } from 'src/features/common/artist/hooks/use-fetch-artist-countries';
import { IExploreFilters } from 'src/features/common/explore/ducks/explore-nfts/explore-nfts.types';
import { useDebouncedCallback } from 'src/hooks/use-debounced-callback';
import { FilterRender } from './filter-render';

const DEFAULT_PAGER = { limit: 6, page: 0 };

interface IProps {
    selected: string[];
    onChange: (key: keyof IExploreFilters, string: string[]) => void;
}

export const CountriesFilterContainer: React.VFC<IProps> = ({ selected, onChange }) => {
    const [showSearch, setShowSearch] = useState(false);
    const [searchValue, setSearchValue] = useState('');
    const [pager, setPager] = useState(DEFAULT_PAGER);
    const { countries, fetchCountries } = useFetchArtistCountries();

    const filteredCountries = useMemo(() => {
        let result = countries;
        if (searchValue.trim().length) {
            result = result.filter((c) => c.toLowerCase().indexOf(searchValue.trim().toLowerCase()) > -1);
        }
        return result;
    }, [countries, searchValue]);

    const data = useMemo(
        () => filteredCountries.slice(0, pager.page * pager.limit + pager.limit).map((c) => ({ id: c, name: c })),
        [filteredCountries, pager.limit, pager.page]
    );

    const loadMore = useCallback(async () => {
        setPager({
            page: pager.page + 1,
            limit: 10,
        });
    }, [pager.page]);

    const onSubmit = useCallback((e) => {
        setPager(DEFAULT_PAGER);
        setSearchValue(e.target.value);
    }, []);

    const handleSearch = useCallback((value: string) => {
        setPager(DEFAULT_PAGER);
        setSearchValue(value);
    }, []);

    const debouncedSearch = useDebouncedCallback(handleSearch, 500);

    useEffect(() => {
        fetchCountries().then((res) => {
            setShowSearch(res.length > 5);
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <FilterRender
            data={data}
            total={filteredCountries.length}
            loadMore={loadMore}
            onSubmit={onSubmit}
            onSearch={debouncedSearch}
            showSearch={showSearch}
            selected={selected}
            onFilterChange={onChange}
            filterName="artistCountry"
        />
    );
};
